import { Component, ComponentFactoryResolver, OnInit, ViewChild, ViewContainerRef, trigger, style, state, transition, animate } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CacheService } from '../../global/ng2-cache/services/cache.service';
import { Subscription } from 'rxjs/Subscription';
import { EmailFilterGroupComponent, EmailFilter } from '../../global/email-filter-group/email-filter-group.component';
import { AutomateJob, AutomateTask, AutomateFilter } from '../../global/models';
import { WizardStep } from '../../global/wizard-steps/wizard-steps.component';
import { AuthService } from '../../services/auth.service';
import { AutomateJobService } from '../../services/automate-job.service';
import { GlobalMessageService } from '../../services/global-message.service';
import { SessionService } from '../../services/session2.service';
import { SkillService } from '../../services/skill.service';
import { RouteTaskComponent } from '../tasks/route-task/route-task.component';
import { RespondTaskComponent } from '../tasks/respond-task/respond-task.component';
import { TrackTaskComponent } from '../tasks/track-task/track-task.component';
import { RespondtrackTaskComponent } from '../tasks/respondtrack-task/respondtrack-task.component';
import { CreateTaskComponent } from '../tasks/create-task/create-task.component';
import { RespondOtterTaskComponent } from '../tasks/respondotter-task/respondotter-task.component';

@Component({
  selector: 'app-skill-detail',
  templateUrl: './skill-detail.component.html',
  styleUrls: ['./skill-detail.component.scss'],
  animations: [
    trigger('draftState',
      [
        state('inactive', style({
          opacity: 0,
          transform: 'scale(.9)'
        })),
        state('active', style({
          opacity: 1,
          transform: 'scale(1)'
        })),
        transition('inactive => active', animate('300ms ease-in'))
      ]
    )
  ]
})
export class SkillDetailComponent implements OnInit {
  @ViewChild('taskContainer', { read: ViewContainerRef }) taskContainer;

  loadingPage: boolean = true;
  session: any;

  automateJob: AutomateJob;
  skillDefinition: any = {};

  wizardSteps: Array<WizardStep> = [];
  selectedStep: WizardStep = null;
  stepSubscription: Subscription = null; // this is used to subscribe to the onChange of the current step
  navSubscription: Subscription = null;

  // for draft saved/status
  draftTimer: any = null;
  draftSaved: string = '';
  draftState: string = 'inactive';

  // for toggle and popover state
  validJob: boolean = true;
  previouslyEnabled: boolean = false;
  toggleStatusOpen: boolean = false;
  toggleStatus: string = '';

  constructor(
    private sessionService: SessionService,
    private router: Router,
    private cacheService: CacheService,
    private skillService: SkillService,
    private globalMessageService: GlobalMessageService,
    private route: ActivatedRoute,
    private componentFactoryResolver: ComponentFactoryResolver,
    private automateJobService: AutomateJobService
  ) { }

  ngOnInit() {
    this.session = this.cacheService.get('session');
    if (!this.session) {
      this.sessionService.getSession(true).subscribe(
        (session: any) => {
          this.session = session;
          this.initView();
        }
      );
    }
    else {
      this.initView();
    }
  }

  initView() {
    this.loadingPage = true; // this will get turned off in buildUI
    this.selectedStep = this.wizardSteps[0];

    let skillId = this.route.snapshot.params['id'];

    if (skillId == 'new') {
      // create a new route; get the type
      let skillTypeId = this.route.snapshot.queryParams['type'];
      this.loadNewSkill(skillTypeId);

    }
    else {
      this.loadExistingSkill(skillId);
    }
  }

  loadNewSkill(typeId: string) {
    this.skillService.getById(typeId).subscribe(
      (result: any) => {
        this.skillDefinition = result;

        // this.automateJob needs to reference a clone of the result
        // otherwise references to the skill definition can overwrite data
        // on the automate job
        let jobDefaults = JSON.parse(JSON.stringify(result));

        this.automateJob = {
          _id: 'new',
          name: `New ${jobDefaults.name}`,
          account_id: this.cacheService.get('session').account_id,
          type: jobDefaults.type,
          enabled: false,
          deleted: false,
          track_changes: false,
          filters: [],
          tasks: []
        };

        jobDefaults.tasks.forEach(t => {
          let task = this.automateJobService.initTask(t.type);
          task.settings = t.settings;
          // need to set invalid state immediately to avoid turning on skills that are invalid
          if (task.type == 'route') {
            task.invalid_reason = 'A route type is required for routing';
          }
          if ((task.type == 'track' || task.type == 'create') && jobDefaults.type != 'routetrack') {
            task.invalid_reason = 'A partner integration must be selected';
          }
          this.automateJob.tasks.push(task);
        });

        jobDefaults.filters.forEach(f => {
          let filter: AutomateFilter = {
            type: f.type,
            include: f.options.default.include,
            exclude: f.options.default.exclude
          };
          this.automateJob.filters.push(filter);
        });

        this.buildUI();
      },
      err => {
        // todo?
      }
    );
  }

  loadExistingSkill(jobId: string) {
    this.automateJobService.getJob(jobId).toPromise()
      .then((job: any) => {
        this.automateJob = job;
        this.previouslyEnabled = job.enabled;
        this.checkValid();
        return this.skillService.getByType(job.type).toPromise();
      })
      .then(skillDefinition => {
        this.skillDefinition = skillDefinition;
        this.buildUI();
      })
      .catch(err => {
        // todo?
      });
  }

  /**
   * Build the UI after initView
   */
  buildUI() {
    this.loadingPage = false;
    this.buildWizard();
    this.loadTaskComponent();
  }

  /**
   * Dynamically build the wizard steps using the skill definition
   */
  buildWizard() {
    // filters
    if (this.skillDefinition.filters && this.skillDefinition.filters.length > 0) {
      this.addWizardStep('emails', EmailFilterGroupComponent, this.getSupportedFiltersForSkill());
    }

    // //tasks
    if (this.skillDefinition.tasks) {

      for (let i = 0; i < this.skillDefinition.tasks.length; i++) {
        let t = this.skillDefinition.tasks[i];
        let options = {
          arrIdx: i,
          settings: t.settings
        };
        this.addWizardStep(t.name, this.getTaskComponentToLoad(t.type), options);
      }
    }

    this.addWizardStep('finish', null, null);
    this.selectedStep = this.wizardSteps[0];
  }

  /**
   * Assumption that every skill will have a filter of some kind - even if they are read only
   * Options will change depending on what filter the skill supports
   */
  getSupportedFiltersForSkill(): EmailFilter[] {
    return this.skillDefinition.filters.map(filter => filter);
  }

  /**
   * Add a step to the wizard control
   * @param name
   * @param component
   * @param options
   */
  addWizardStep(name: string, component: any, options: any) {
    // We want some custom labels for wizard steps that are different for the same core skill. For example, for Route & Track we want
    // the step to say "Track", but for Update (which also uses the track skill) we want it to say "Update". We don't currently have a way
    // to do this dynamically and should figure something out at some point
    if (name.toLowerCase() == 'tracking' && this.automateJob.type == 'updatecontacts') {
      name = 'Update';
    }

    let index = this.wizardSteps.length + 1;
    let step: WizardStep = {
      stepNumber: index++,
      label: name,
      component: component,
      options: options
    };
    this.wizardSteps.push(step);
  }

  /**
  * Change wizard step
  * @param step
  */
  changeStep(step: WizardStep) {
    this.selectedStep = step;
    this.loadTaskComponent();
  }

  /**
   * Load the task in this.selectedStep into the UI
   */
  loadTaskComponent() {
    if (this.stepSubscription) {
      this.stepSubscription.unsubscribe();
      delete this.stepSubscription;
    }
    if (this.navSubscription) {
      this.navSubscription.unsubscribe();
      delete this.navSubscription;
    }

    // a special case for 'finish'
    if (this.selectedStep.label == 'finish') {
      // kick off an aysnc save just so we make sure to grab any chanes
      // this.router.navigate(['/automate']);
      window.location.href =  '/automation/skills';
      return;
    }

    let componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.selectedStep.component);
    this.taskContainer.clear();
    let componentRef = this.taskContainer.createComponent(componentFactory);

    // remember: this.automateJob references a clone of the same data
    // that this.selectedStep references. this is so the 'options' object
    // doesn't get overwritten when the job changes.

    // 'emails' needs a special case bacause its data comes from a
    // different location in the SkillDefinition than a task's data does

    // the subscribtion function below don't work unless they are arrow function.
    // for some reason, the 'this' scope is changed when we use a reference to a
    // function on this class.

    if (this.selectedStep.label == 'emails') {
      componentRef.instance.filters = this.automateJob.filters;
      componentRef.instance.jobName = this.automateJob.name;
      componentRef.instance.options = this.selectedStep.options;

      this.stepSubscription = (componentRef.instance).onChange.subscribe((eventData: any) => {
        this.automateJob.filters = eventData.filters;
        this.automateJob.name = eventData.jobName;
        this.stage();
      });
    }
    else {
      // no more special cases. we can find the information we need in skillDefinition.tasks
      componentRef.instance.task = this.automateJob.tasks[this.selectedStep.options.arrIdx];

      // Added 5.0.7
      // Longer-term solution may be needed at some point. We need a property on tasks telling us whether they are
      // required for the skill. In the case of R&T, track is optional. We need a way to mark the skill as valid
      // even if a task isn't configured within the skill. Default to being required with exceptions noted here
      componentRef.instance.task.required = true;
      if (this.automateJob.type == 'routetrack' && componentRef.instance.task.type == 'track') {
        componentRef.instance.task.required = false;
      }

      this.stepSubscription = (componentRef.instance).onChange.subscribe((task: AutomateTask) => {
        task.updated_at = new Date();
        if (task.invalid_reason) {
          this.automateJob.enabled = false;
        }

        this.automateJob.tasks[this.selectedStep.options.arrIdx] = task;
        this.stage();
      });

      this.navSubscription = (componentRef.instance).onNavChange.subscribe((next: boolean) => {
        if (next) {
          this.nextStep();
        }
        else {
          this.prevStep();
        }
      });
    }
  }

  /**
   * Find an automateJob task with the given type
   * @param type
   */
  findTaskByType(type: string) {
    for (let i = 0; i < this.automateJob.tasks.length; i++) {
      if (this.automateJob.tasks[i].type == type) {
        return this.automateJob.tasks[i];
      }

      return null;
    }
  }

  /**
   * convert a task type to an angular component
   * @param taskType
   */
  getTaskComponentToLoad(taskType: string) {
    switch (taskType) {
      case 'respond':
        return RespondTaskComponent;
      case 'respondtrack':
        return RespondtrackTaskComponent;
      case 'otterrespond':
        return RespondOtterTaskComponent;
      case 'route':
        return RouteTaskComponent;
      case 'track':
        return TrackTaskComponent;
      case 'create':
        return CreateTaskComponent;
    }
  }

  /**
   * Toggle the job on/off from the editor
   */
  toggle() {
    this.automateJob.enabled = !this.automateJob.enabled;
    this.automateJob.track_changes = true;

    // if someone manually toggles a job off we don't need to show the popop indicator
    if (!this.automateJob.enabled) {
      this.previouslyEnabled = false;
    }

    this.stage();
  }

  /**
   * Manages valid state for the toggle switch
   */
  private checkValid() {
    this.validJob = true;
    this.automateJob.tasks.forEach((task: AutomateTask) => {
      if (task.invalid_reason && task.invalid_reason.length > 0) {
        this.validJob = false;
      }
    });
  }

  /**
   * Stage changes to be saved shortly
   */
  private stage() {
    this.checkValid();

    if (this.previouslyEnabled && !this.automateJob.enabled) {
      // job was enabled during this editing session but has been disabled due to editing, send the popover
      this.toggleStatus = `Skill has been turned off due to a change you made`;
      this.toggleStatusOpen = true;
      this.previouslyEnabled = false;
    }

    // when someone turns the job on from within the editor we need to know so if they invalidate later in the same session
    // the popover will show
    if (this.automateJob.enabled) {
      this.previouslyEnabled = true;
    }

    if (this.draftTimer) {
      clearTimeout(this.draftTimer);
    }
    this.draftTimer = setTimeout(() => {
      this.save();
    }, 1500);
  }

  private save() {
    this.draftState = 'inactive';

    this.automateJobService.saveJob(this.automateJob).subscribe(
      (result: any) => {
        this.automateJob = result;
        this.draftSaved = new Date().toLocaleTimeString();
        this.draftState = 'active';
      },
      error => {
        this.globalMessageService.error();
      }
    );
  }


  nextStep() {
    // stepNumber starts at 1 but the array starts at 0.
    // confusing, but it means we just say our next step index is our current step number...
    this.changeStep(this.wizardSteps[this.selectedStep.stepNumber]);
  }

  prevStep() {
    // stepNumber starts at 1 but the array starts at 0.
    // confusing, but it means we just say our prev step index is our current step number -2...
    this.changeStep(this.wizardSteps[this.selectedStep.stepNumber - 2]);
  }

  isPrevDisabled() {

    return !this.selectedStep || this.selectedStep.stepNumber == 1;
  }

  isNextDisabled() {
    return !this.selectedStep || this.selectedStep.stepNumber == this.wizardSteps.length;
  }

}



// WEBPACK FOOTER //
// ./client/app/automate/skill-detail/skill-detail.component.ts